import { useEffect, useRef, useState } from 'react';
import { commandId } from './commandId.ts';
import {
  pageMatches,
  readNotebook,
  type PageDraft,
  type TutorPage,
} from './tutorNotebook.ts';
export function useTutorNotebook(sessionId: string | undefined) {
  const [pages, setPages] = useState<TutorPage[]>([]),
    [activeId, setActiveId] = useState<string>(),
    [open, setOpen] = useState(false),
    [notice, setNotice] = useState('');
  const state = useRef<{ sessionId?: string; pages: TutorPage[] }>({
    pages: [],
  });
  const key = () =>
    sessionId ? `enactry.tutor-notebook.v1:${sessionId}` : undefined;
  const commit = (next: TutorPage[]) => {
    state.current = { sessionId, pages: next };
    setPages(next);
    try {
      const k = key();
      if (k) localStorage.setItem(k, JSON.stringify(next));
    } catch {
      setNotice(
        'These notes are available for this visit. Browser storage is full or unavailable.',
      );
    }
  };
  useEffect(() => {
    let next: TutorPage[] = [];
    try {
      const k = key();
      next = k ? readNotebook(localStorage.getItem(k)) : [];
    } catch {}
    state.current = { sessionId, pages: next };
    setPages(next);
    setActiveId(undefined);
    setOpen(false);
  }, [sessionId]);
  function add(draft: PageDraft) {
    if (!sessionId) return;
    const existing = state.current.pages.find((p) => pageMatches(p, draft));
    if (existing) {
      setActiveId(existing.id);
      setOpen(true);
      return existing;
    }
    if (state.current.pages.length >= 100) {
      setNotice(
        'Your notebook has 100 pages. Delete an unneeded page before saving another.',
      );
      return;
    }
    const page: TutorPage = {
      ...draft,
      id: commandId(),
      createdAt: Date.now(),
    };
    commit([...state.current.pages, page]);
    setActiveId(page.id);
    setOpen(true);
    return page;
  }
  function patch(id: string, change: Partial<TutorPage>) {
    commit(
      state.current.pages.map((p) => (p.id === id ? { ...p, ...change } : p)),
    );
  }
  function select(id: string) {
    if (state.current.pages.some((p) => p.id === id)) {
      setActiveId(id);
      setOpen(true);
    }
  }
  function remove(id: string) {
    const removed = state.current.pages.find((p) => p.id === id);
    commit(state.current.pages.filter((p) => p.id !== id));
    setActiveId(state.current.pages.at(-1)?.id);
    if (!state.current.pages.length) setOpen(false);
    return removed;
  }
  function restore(page: TutorPage) {
    commit(
      [...state.current.pages, page].sort((a, b) => a.createdAt - b.createdAt),
    );
    select(page.id);
  }
  return {
    pages,
    active: pages.find((p) => p.id === activeId),
    open,
    setOpen,
    add,
    patch,
    select,
    remove,
    restore,
    notice,
  };
}
